import { useQuery } from "@tanstack/react-query";
import { capitalize, compact, groupBy, map } from "lodash/fp";

import { queryMetricsForResult } from "@app/analytics/hooks/metrics/useMetricsQuery";
import * as Filters from "@app/analytics/meetops/filters";
import { DateRange, MeetOpsQuery } from "@app/analytics/meetops/types";
import { getQueryKeyFromDateRanges } from "@app/analytics/utils";
import type { ParsedQueryResultRequiredBy } from "@app/analytics/utils";
import useAuth from "@app/common/auth/useAuth";
import { UserMeeting } from "@app/common/types";
import { User } from "@app/common/types";
import {
  EnterpriseTeam,
  EnterpriseWorkspace,
} from "@app/common/workspaces/types";

type MeetingSeriesQueryResult = ParsedQueryResultRequiredBy<
  "meetingSeriesId" | "meetingScore" | "sentiment" | "engagement"
>;
type KeywordsQueryResult = ParsedQueryResultRequiredBy<
  "sessionLevelTopic" | "meetingSeriesId" | "numInstancesBySession"
>;

const getMeetingSeriesQuery = (
  dateRanges: DateRange[],
  userId?: User["id"],
  teamId?: EnterpriseTeam["id"],
  workspaceId?: EnterpriseWorkspace["id"]
): MeetOpsQuery => ({
  dateRanges,
  dimensions: ["meetingSeriesId"],
  filters: [Filters.isRecurringMeeting()],
  metrics: ["meetingScore", "sentiment", "engagement"],
  people: userId ? "only_me" : "everyone",
  userId,
  teamId,
  workspaceId,
});

const getKeywordsQuery = (
  dateRanges: DateRange[],
  seriesIds: string[],
  userId?: User["id"],
  teamId?: EnterpriseTeam["id"],
  workspaceId?: EnterpriseWorkspace["id"]
): MeetOpsQuery => ({
  dateRanges,
  dimensions: ["sessionLevelTopic", "meetingSeriesId"],
  filters: [Filters.isInSeries(seriesIds)],
  metrics: ["numInstancesBySession"],
  people: userId ? "only_me" : "everyone",
  userId,
  teamId,
  workspaceId,
});

const getKeywordsForMeetings = async ({
  userId,
  teamId,
  isForCurrentUser,
  dateRanges,
  seriesIds,
  workspaceId,
}: {
  userId?: User["id"];
  teamId?: EnterpriseTeam["id"];
  isForCurrentUser: boolean;
  dateRanges: DateRange[];
  seriesIds: string[];
  workspaceId?: EnterpriseWorkspace["id"];
}): Promise<{ [seriesId: string]: KeywordsQueryResult[] }> => {
  const metricsQuery = await queryMetricsForResult(
    getKeywordsQuery(dateRanges, seriesIds, userId, teamId, workspaceId),
    isForCurrentUser
  );

  return groupBy(
    "meetingSeriesId",
    (metricsQuery.results?.[0]?.data ?? []).filter(
      (data) => (data.numInstancesBySession?.rawValue ?? 0) > 1
    )
  );
};

const getRecurringMeetings = async ({
  userId,
  teamId,
  workspaceId,
  isForCurrentUser,
  dateRanges,
}: {
  userId?: User["id"];
  teamId?: EnterpriseTeam["id"];
  workspaceId?: EnterpriseWorkspace["id"];
  isForCurrentUser: boolean;
  dateRanges: DateRange[];
}): Promise<Partial<UserMeeting>[]> => {
  const metricsQueryData = await queryMetricsForResult(
    getMeetingSeriesQuery(dateRanges, userId, teamId, workspaceId),
    isForCurrentUser
  );

  return (metricsQueryData.results?.[0]?.data ?? []).map(
    (rawMeeting: MeetingSeriesQueryResult) => ({
      seriesId: rawMeeting.meetingSeriesId!,
      meetingScore: rawMeeting.meetingScore.value ?? undefined,
      sentiment: rawMeeting.meetingScore.value ?? undefined,
      engagement: rawMeeting.engagement.value ?? undefined,
    })
  );
};

const useRecurringMeetings = (
  {
    dateRanges,
    userId,
    teamId,
  }: {
    dateRanges: DateRange[];
    userId?: User["id"];
    teamId?: EnterpriseTeam["id"];
  },
  options: { enabled?: boolean }
) => {
  const { user } = useAuth();
  const isForCurrentUser = Boolean(userId) && user?.id === userId;

  const query = useQuery({
    queryKey: [
      "recurringMeetings",
      userId,
      teamId,
      ...getQueryKeyFromDateRanges(dateRanges),
    ],
    queryFn: async () => {
      const meetings = await getRecurringMeetings({
        isForCurrentUser,
        userId,
        teamId,
        dateRanges,
        workspaceId: user?.workspaceId,
      });

      const seriesIds: string[] = compact(map("seriesId", meetings));

      const keywordsByMeetingSeries = await getKeywordsForMeetings({
        isForCurrentUser,
        userId,
        teamId,
        dateRanges,
        seriesIds,
        workspaceId: user?.workspaceId,
      });

      return meetings.map((meeting) => {
        meeting.keywords = (
          keywordsByMeetingSeries[meeting.seriesId!] ?? []
        ).map((keywordResult) => capitalize(keywordResult.sessionLevelTopic!));
        return meeting;
      });
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: 2 * 60 * 1000,
    enabled: (options.enabled ?? true) && Boolean(dateRanges),
  });

  return {
    ...query,
    meetings: query.data ?? [],
  };
};

export default useRecurringMeetings;
